import { View } from 'react-native';
import { Box } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import type { CalendarEvent } from '../../../types/calendar';
import type { LocalDate } from '../utils/calendarDate';
import {
  DEFAULT_DAY_WINDOW,
  getEventGeometry,
  getNowLineRatio,
  type TimeWindow,
} from '../utils/calendarDayGeometry';
import { getEventBlockLayout } from '../utils/calendarEventLayout';
import { buildHourRows, getGridHeight, HOUR_HEIGHT } from '../utils/calendarHourGrid';
import { assignOverlapLanes } from '../utils/calendarOverlap';
import { CalendarNowMarker } from './CalendarNowMarker';
import { DraggableEventBlock } from './DraggableEventBlock';

interface CalendarWeekDayColumnProps {
  date: LocalDate;
  /** Already filtered to this day's TIMED (non-all-day) events by the caller
   * (CalendarWeekGrid), which owns the week-spanning all-day strip itself. */
  events: CalendarEvent[];
  timeZone: string;
  now: Date;
  /** The slice of the day to render. Every column of one week grid MUST be
   * handed the same window — they share a single hour gutter. */
  window?: TimeWindow;
  onEventPress?: (event: CalendarEvent) => void;
  /** Desktop drag-to-reschedule; see useEventDrag for why mobile has none. */
  onReschedule?: (event: CalendarEvent, span: { startsAt: string; endsAt: string }) => void;
}

// One day-column of the week grid: the same vertical geometry
// (calendarDayGeometry.ts) and the same CalendarDayEventBlock interaction
// component as CalendarDayTimeline (Unit 4), unchanged — only the horizontal
// placement is new, from calendarOverlap.ts's lane assignment.
export function CalendarWeekDayColumn({
  date,
  events,
  timeZone,
  now,
  window = DEFAULT_DAY_WINDOW,
  onEventPress,
  onReschedule,
}: Readonly<CalendarWeekDayColumnProps>) {
  const lanes = assignOverlapLanes(events);
  const nowRatio = getNowLineRatio(date, timeZone, now, window);
  const hourRows = buildHourRows(window);
  const gridHeight = getGridHeight(window);

  return (
    <Box
      testID={`calendar_week_day_column_${date.year}-${date.month}-${date.day}`}
      flex={1}
      style={{ position: 'relative', borderLeftWidth: 1, borderColor: colors.border.muted }}
    >
      <View>
        {hourRows.map((hour) => (
          <View
            key={hour}
            testID={`calendar_week_hour_row_${hour}`}
            style={{ height: HOUR_HEIGHT, borderBottomWidth: 1, borderColor: colors.border.muted }}
          />
        ))}
      </View>

      <View
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        pointerEvents="box-none"
      >
        {nowRatio !== null && (
          <CalendarNowMarker ratio={nowRatio} testID="calendar_week_now_line" />
        )}

        {lanes.map(({ event, lane, laneCount }) => {
          const geometry = getEventGeometry(event.startsAt, event.endsAt, date, timeZone, window);
          if (!geometry) return null;

          const layout = getEventBlockLayout(geometry, lane, laneCount, gridHeight);

          return (
            <DraggableEventBlock
              key={event.id}
              event={event}
              date={date}
              timeZone={timeZone}
              window={window}
              gridHeight={gridHeight}
              layout={layout}
              clippedStart={geometry.clippedStart}
              clippedEnd={geometry.clippedEnd}
              onPress={onEventPress}
              onReschedule={onReschedule}
            />
          );
        })}
      </View>
    </Box>
  );
}
